import axios from 'axios';
import { BaseService } from './base.service';
import {
  ContentItem,
  ContentItemFormData,
  ContentItemListParams,
  ContentItemListResult,
  ContentStatus,
  VideoUploadTarget,
} from '@/types/contentItem';

class ContentItemService extends BaseService {
  constructor() {
    super('/content-library/items');
  }

  async listContentItems(params: ContentItemListParams): Promise<ContentItemListResult> {
    const query = new URLSearchParams();
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.status) query.set('status', params.status);
    if (params.mediaType) query.set('mediaType', params.mediaType);
    if (params.categoryId) query.set('categoryId', params.categoryId);
    if (params.tag) query.set('tag', params.tag);
    if (params.archetypeId) query.set('archetypeId', params.archetypeId);
    if (params.tpiScreenId) query.set('tpiScreenId', params.tpiScreenId);
    if (params.sbGoalId) query.set('sbGoalId', params.sbGoalId);
    if (params.equipmentId) query.set('equipmentId', params.equipmentId);
    if (params.patternId) query.set('patternId', params.patternId);
    if (params.positionId) query.set('positionId', params.positionId);
    if (params.search) query.set('search', params.search);
    const qs = query.toString();
    return this.get<ContentItemListResult>(qs ? `?${qs}` : '');
  }

  async getContentItem(id: string): Promise<ContentItem> {
    return this.get<ContentItem>(`/${id}`);
  }

  async createContentItem(data: ContentItemFormData): Promise<ContentItem> {
    return this.post<ContentItem>('', data);
  }

  async updateContentItem(id: string, data: ContentItemFormData): Promise<ContentItem> {
    return this.put<ContentItem>(`/${id}`, data);
  }

  async updateContentItemStatus(id: string, status: ContentStatus): Promise<ContentItem> {
    return this.patch<ContentItem>(`/${id}/status`, { status });
  }

  async deleteContentItem(id: string): Promise<void> {
    return this.delete<void>(`/${id}`);
  }

  async uploadContentItemImage(id: string, file: File): Promise<ContentItem> {
    const formData = new FormData();
    formData.append('file', file);
    return this.post<ContentItem>(`/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async requestVideoUploadUrl(id: string): Promise<VideoUploadTarget> {
    return this.post<VideoUploadTarget>(`/${id}/video/upload-url`);
  }

  // This deliberately bypasses BaseService/the shared `api` axios instance —
  // both prepend the app's API base URL and attach the box-os admin JWT
  // bearer auth header. Sending that credential to Gumlet's upload host
  // would leak an admin secret to a third party (T-314-01). Never log
  // `uploadUrl` — it is a bearer-equivalent pre-signed credential (T-314-02).
  async putVideoFile(uploadUrl: string, file: File): Promise<void> {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });
  }
}

export const contentItemService = new ContentItemService();
